import { LiveData, Store } from '@toeverything/infra';
import { exhaustMap } from 'rxjs';

import {
  AuthService,
  type DefaultServerService,
  type WorkspaceServerService,
} from '../../cloud';
import type { GlobalState } from '../../storage';
import type { WorkspaceService } from '../../workspace';
import { type DevtoConfig } from '../type';

export class DevtoStore extends Store {
  constructor(
    private readonly globalState: GlobalState,
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly defaultServerService: DefaultServerService
  ) {
    super();
  }

  // User-level storage key (not workspace-specific)
  private _getKey(userId: string) {
    return `devto:${userId}`;
  }

  // Use workspace server if available, otherwise fall back to default server
  private get server() {
    return this.workspaceServerService.server || this.defaultServerService.server;
  }

  authService = this.server?.scope.get(AuthService);

  userId$ =
    this.workspaceService.workspace.meta.flavour === 'local' ||
    !this.authService
      ? new LiveData('__local__')
      : this.authService.session.account$.map(
          account => account?.id ?? '__local__'
        );

  getUserId() {
    // Re-get authService in case it wasn't available during construction
    const authService = this.server?.scope.get(AuthService);
    return this.workspaceService.workspace.meta.flavour === 'local' ||
      !authService
      ? '__local__'
      : (authService.session.account$.value?.id ?? '__local__');
  }

  storageKey$() {
    return this.userId$.map(userId => this._getKey(userId));
  }

  getStorageKey() {
    const userId = this.getUserId();
    return this._getKey(userId);
  }

  watchSetting() {
    return this.storageKey$().pipe(
      exhaustMap(storageKey => {
        return this.globalState.watch<DevtoConfig>(storageKey);
      })
    );
  }

  getSetting(): DevtoConfig | undefined;
  getSetting<Key extends keyof DevtoConfig>(
    key: Key
  ): DevtoConfig[Key] | undefined;
  getSetting(key?: keyof DevtoConfig) {
    const config = this.globalState.get<DevtoConfig>(this.getStorageKey());
    if (!key) return config;
    return config?.[key];
  }

  setSetting<Key extends keyof DevtoConfig>(
    key: Key,
    value: DevtoConfig[Key]
  ) {
    this.globalState.set(this.getStorageKey(), {
      ...this.getSetting(),
      [key]: value,
    });
  }

  setSettings(settings: Partial<DevtoConfig>) {
    this.globalState.set(this.getStorageKey(), {
      ...this.getSetting(),
      ...settings,
    });
  }

  clearSettings() {
    this.globalState.del(this.getStorageKey());
  }
}
